import React from "react";
import { Avatar, Box, IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import { APP_HEADER_LOGO_TEXT, APP_PUBLIC_URL } from "../config";
import { SUPPORTED_LOCALES, t } from "../i18n";
import oceanLogo from "../assets/ocean-logo.png";

export default function SkeletonPageHeader({ onToggleTheme, onOpenAccount, onOpenAdminPanel, onSignOut, onLocaleChange, currentLocale = "ru", currentTheme = "light", isAdmin = false, accountName = "U", accountAvatarUrl = "" }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);
  const avatarBg = currentTheme === "dark" ? "#9a948b" : "#9ca3af";

  return (
    <Box sx={{ position: "sticky", top: 0, zIndex: 1200, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, py: 2, px: { xs: 1.5, sm: 2, md: 3 }, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
      <a href={APP_PUBLIC_URL} style={{ textDecoration: "none", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box component="img" src={oceanLogo} alt="OceanConnect" sx={{ width: { xs: 28, md: 34 }, height: "auto", display: "block", flexShrink: 0 }} />
          <Box sx={{ fontWeight: "bold", fontSize: { xs: 22, md: 28 }, color: "text.primary", fontFamily: "RodondoRUS, sans-serif" }}>
            {APP_HEADER_LOGO_TEXT}
          </Box>
        </Box>
      </a>

      <Box sx={{ display: "flex", gap: { xs: 0.25, sm: 0.5, md: 1 }, flexShrink: 0, alignItems: "center" }}>
        <Tooltip title={t("common.language", currentLocale)} arrow>
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ color: "text.primary" }}>
            <TranslateIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          {SUPPORTED_LOCALES.map((locale) => (
            <MenuItem
              key={locale}
              selected={locale === currentLocale}
              onClick={() => {
                onLocaleChange?.(locale);
                setAnchorEl(null);
              }}
            >
              {t(`language.${locale}`, currentLocale)}
            </MenuItem>
          ))}
        </Menu>
        {isAdmin && (
          <Tooltip title={currentLocale === "en" ? "Admin panel" : "Панель администратора"} arrow>
            <IconButton onClick={() => onOpenAdminPanel?.("general")} sx={{ color: "text.primary" }}>
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t("shell.changeTheme", currentLocale)} arrow>
          <IconButton onClick={onToggleTheme} sx={{ color: "text.primary" }}>
            {currentTheme === "dark" ? <LightModeIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title={t("shell.accountMenu", currentLocale)} arrow>
          <IconButton onClick={(event) => setProfileAnchorEl(event.currentTarget)} sx={{ p: 0.35 }}>
            <Avatar src={accountAvatarUrl || ""} sx={{ width: 34, height: 34, bgcolor: avatarBg, color: "#fff", fontSize: 15, fontWeight: 600 }}>
              {accountName}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Menu anchorEl={profileAnchorEl} open={Boolean(profileAnchorEl)} onClose={() => setProfileAnchorEl(null)}>
          <MenuItem onClick={() => { onOpenAccount?.(); setProfileAnchorEl(null); }}>
            <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
            {t("shell.account", currentLocale)}
          </MenuItem>
          <MenuItem onClick={() => { onSignOut?.(); setProfileAnchorEl(null); }}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            {t("shell.signOut", currentLocale)}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
